// "Edit in planner": the Import files dialog hands one file's lines and highlights to
// /hike-planner, which imports them with its own importFile action — so nothing is saved
// until the user saves the plan, exactly as when a file is imported inside the planner.
//
// The lines travel as a GeoJSON file (the planner reads GeoJSON already, whatever format the
// original was — FIT and KMZ included), one LineString per day. The waypoints travel beside
// it rather than inside, so the planner doesn't ask "route, waypoints or both?" a second time.

import type { ImportedWaypoint } from '../helpers/ImportFileHelper';
import type { ImportDay } from './importPayloads';
import type { ImportSavedResult } from './importSave';
import type { ImportWaypoint } from './importTypes';

export const PLANNER_HANDOFF_KEY = 'hiiker:import-files:planner';

/** The other files' result when Edit in planner changes the page; the planner shows it. */
export const IMPORT_RESULT_KEY = 'hiiker:import-files:result';

export interface PlannerHandoff {
  /** The GeoJSON file's name: the original's base name, so the planner names routes after it. */
  fileName: string;
  geojson: { type: 'FeatureCollection'; features: unknown[] };
  waypoints: ImportedWaypoint[];
}

export function plannerHandoff(title: string, days: ImportDay[], waypoints: ImportWaypoint[]): PlannerHandoff {
  return {
    fileName: `${title}.geojson`,
    geojson: {
      type: 'FeatureCollection',
      features: days
        .filter(d => d.points.length >= 2)
        .map(d => ({
          type: 'Feature',
          properties: d.name ? { name: d.name } : {},
          geometry: { type: 'LineString', coordinates: d.points.map(p => (p.ele != null ? [p.lon, p.lat, p.ele] : [p.lon, p.lat])) }
        }))
    },
    waypoints: waypoints.map(w => ({ name: w.name.trim() || undefined, type: 'other', description: '', lng: w.point.lon, lat: w.point.lat }))
  };
}

/** Stores the hand-off for the planner page. False when the browser won't hold it. */
export function storePlannerHandoff(handoff: PlannerHandoff, storage: Pick<Storage, 'setItem'> = window.sessionStorage): boolean {
  try {
    storage.setItem(PLANNER_HANDOFF_KEY, JSON.stringify(handoff));
    return true;
  } catch {
    return false;
  }
}

/** A hand-off as what the planner's importFile takes: a GeoJSON File, plus the highlights. */
export function handoffFile(handoff: PlannerHandoff): { file: File; waypoints: ImportedWaypoint[] } | null {
  if (!handoff?.geojson || !Array.isArray(handoff.geojson.features)) return null;
  const file = new File([JSON.stringify(handoff.geojson)], handoff.fileName || 'import.geojson', { type: 'application/geo+json' });
  return { file, waypoints: Array.isArray(handoff.waypoints) ? handoff.waypoints : [] };
}

/** The planner's side: the waiting hand-off, read once. */
export function takePlannerHandoff(
  storage: Pick<Storage, 'getItem' | 'removeItem'> = window.sessionStorage
): { file: File; waypoints: ImportedWaypoint[] } | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(PLANNER_HANDOFF_KEY);
    storage.removeItem(PLANNER_HANDOFF_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return handoffFile(JSON.parse(raw) as PlannerHandoff);
  } catch {
    return null;
  }
}

/** Keeps a save's result for the planner page to show. False when the browser won't hold it. */
export function storeImportResult(result: ImportSavedResult, storage: Pick<Storage, 'setItem'> = window.sessionStorage): boolean {
  try {
    storage.setItem(IMPORT_RESULT_KEY, JSON.stringify(result));
    return true;
  } catch {
    return false;
  }
}

/** The planner's side: the waiting result, read once; null when there's none or it's unreadable. */
export function takeImportResult(storage: Pick<Storage, 'getItem' | 'removeItem'> = window.sessionStorage): ImportSavedResult | null {
  try {
    const raw = storage.getItem(IMPORT_RESULT_KEY);
    storage.removeItem(IMPORT_RESULT_KEY);
    const result = raw ? JSON.parse(raw) : null;
    return result && Array.isArray(result.created) && Array.isArray(result.failures) && result.counts ? result : null;
  } catch {
    return null;
  }
}

/**
 * Set by an open planner page: Import files opened inside the planner (from its Waypoints
 * sheet) hands the file straight to it rather than leaving the plan being edited.
 */
export type PlannerImportHook = (handoff: { file: File; waypoints: ImportedWaypoint[] }) => Promise<void>;

declare global {
  interface Window {
    hiikerPlannerImport?: PlannerImportHook;
  }
}
